import {
  type HealthEventSummary,
  RevenueMetrics,
  SiteMetrics,
} from "@inrange/building-manager-api-client";
import { Tooltip } from "@inrange/theme-components";
import { formatCurrency } from "@inrange/theme-components/formatting";
import React, { ReactNode, useMemo, useState } from "react";
import { Alert, Offcanvas, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  formatNumber,
  formatPct,
  formatPctSigned,
  RYG_ICONS,
} from "./deploymentStatsUtils";
import { STATUS_OPTIONS, STATUS_WEIGHTS } from "./site/operationalStatusUtils";
import stringToReactNodesWithLinks from "./site/siteSections/stringToReactNodesWithLinks";

// ── Helpers ─────────────────────────────────────────────────────────

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "";
  const d = new Date(value);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const RYG_ORDER: Record<string, number> = { red: 0, amber: 1, green: 2, blue: 3 };

const fmtWithPct = (
  forecastVal: number | undefined,
  pct: number | null | undefined
) => {
  if (forecastVal === undefined) return "";
  const fmtVal = formatNumber(forecastVal, 1);
  if (pct === null || pct === undefined) return fmtVal;
  return `${fmtVal} (${formatPctSigned(pct)})`;
};

const fmtPctWithPpDiff = (
  forecastPct: number | undefined,
  ppDiff: number | null | undefined
) => {
  if (forecastPct === undefined) return "";
  const fmtVal = formatPct(forecastPct);
  if (ppDiff === null || ppDiff === undefined) return fmtVal;
  return `${fmtVal} (${formatPctSigned(ppDiff)})`;
};

/** Format a currency forecast value alongside the actual-vs-forecast pct. */
const fmtCurrencyWithPct = (
  gbpVal: number | undefined,
  nativeVal: number | null | undefined,
  pct: number | null | undefined,
  currencyCode: string | undefined
) => {
  if (gbpVal === undefined) return "";
  const gbpStr = formatCurrency(gbpVal, "GBP", 0, false);
  const native = nativeVal != null ? nativeVal : null;
  const main = formatWithNativeCurrency(gbpStr, native, currencyCode);
  if (pct === null || pct === undefined) return main;
  return `${main} (${formatPctSigned(pct)})`;
};

/** Format a GBP value, optionally prepending the native currency equivalent. */
const formatWithNativeCurrency = (
  gbpFormatted: string,
  nativeValue: number | null | undefined,
  currencyCode: string | undefined
): string => {
  if (nativeValue != null && currencyCode) {
    const nativeFormatted = formatCurrency(nativeValue, currencyCode, 0, false);
    return `${nativeFormatted} (${gbpFormatted})`;
  }
  return gbpFormatted;
};

// ── Small components ────────────────────────────────────────────────

type SortField =
  | "name"
  | "metric"
  | "cod"
  | "ryg"
  | "p50"
  | "p90"
  | "months"
  | "status"
  | `sort_${string}`;

const SortableHeader = ({
  field,
  label,
  align = "right",
  fontWeight,
  sortField,
  sortAsc,
  onToggle,
}: {
  field: SortField;
  label: React.ReactNode;
  align?: "left" | "right" | "center";
  fontWeight?: "normal" | "bold";
  sortField: SortField;
  sortAsc: boolean;
  onToggle: (field: SortField) => void;
}) => {
  const indicator =
    sortField === field ? (sortAsc ? " \u25B2" : " \u25BC") : "";
  return (
    <th
      style={{ cursor: "pointer", textAlign: align, fontWeight }}
      onClick={() => onToggle(field)}
    >
      {label}
      {indicator}
    </th>
  );
};

const RYG_CIRCLE: Record<string, string> = {
  red: "\uD83D\uDD34",
  amber: "\uD83D\uDFE0",
};

const HealthEventList = ({ events }: { events: HealthEventSummary[] }) => (
  <ul className="mb-0 small ps-3" style={{ maxWidth: "min(500px, 40vw)" }}>
    {events.map((e, i) => (
      <li key={i} style={{ wordBreak: "break-word" }}>
        {RYG_CIRCLE[e.severity] ?? ""} {stringToReactNodesWithLinks(e.summary)}
      </li>
    ))}
  </ul>
);

const LiveStatusCell = ({ site }: { site: SiteMetrics }) => {
  const events = site.healthDetail?.liveEvents ?? [];
  if (events.length === 0) {
    return <span className="badge bg-success">Healthy</span>;
  }
  return <HealthEventList events={events} />;
};

const BillingAnalysisCell = ({ site }: { site: SiteMetrics }) => {
  const events = site.healthDetail?.monthlyEvents ?? [];
  const month = site.healthDetail?.analysisMonth;
  if (events.length === 0) {
    return (
      <span className="text-muted">{month ? `${month}: None` : "No data"}</span>
    );
  }
  return <HealthEventList events={events} />;
};

// ── Metric column configs for simple tiles ──────────────────────────

interface MetricColumn {
  label: string;
  key: keyof SiteMetrics;
  format: (value: any, currencyCode: string) => string;
  filter?: (site: SiteMetrics) => boolean;
  getValue?: (site: SiteMetrics) => any;
  getNativeValue?: (site: SiteMetrics) => number | null;
}

const getSiteGbpRevenue = (
  site: SiteMetrics,
  prob: "p50" | "p90",
  field: keyof RevenueMetrics
): number => site.financials?.GBP?.[prob]?.[field] ?? 0;

const getSiteNativeRevenue = (
  site: SiteMetrics,
  prob: "p50" | "p90",
  field: keyof RevenueMetrics
): number | null => {
  const cc = site.currencyCode;
  if (!cc || cc === "GBP") return null;
  return site.financials?.[cc]?.[prob]?.[field] ?? null;
};

const METRIC_COLUMNS: Record<string, MetricColumn> = {
  operationalSites: {
    label: "Site",
    key: "name",
    format: (v: string) => v,
  },
  inProgressSites: {
    label: "Site",
    key: "name",
    format: (v: string) => v,
  },
  pvMw: {
    label: "PV kW",
    key: "pvKw",
    format: (v: number) => formatNumber(v, 1),
    filter: (s) => (s.pvKw ?? 0) > 0,
  },
  batteryMwh: {
    label: "Battery MWh",
    key: "batteryMwh",
    format: (v: number) => formatNumber(v, 2),
    filter: (s) => (s.batteryMwh ?? 0) > 0,
  },
  demandMwh: {
    label: "Demand MWh",
    key: "name",
    format: (v: number) => formatNumber(v, 1),
    filter: (s) => s.energy?.p50?.demandMwh !== undefined,
    getValue: (s) => s.energy?.p50?.demandMwh ?? 0,
  },
  grossRevenue: {
    label: "Gross Contracted Annual Revenue",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    getValue: (s) => getSiteGbpRevenue(s, "p90", "grossRevenue"),
    getNativeValue: (s) => getSiteNativeRevenue(s, "p90", "grossRevenue"),
  },
  arbitrage: {
    label: "Gross Upfront Revenue",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    getValue: (s) => getSiteGbpRevenue(s, "p90", "arbitrage"),
    getNativeValue: (s) => getSiteNativeRevenue(s, "p90", "arbitrage"),
  },
  licenseGrossRevenue: {
    label: "Licence Gross Contracted Annual Revenue",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    filter: (s) => s.investmentModel === "license",
    getValue: (s) => getSiteGbpRevenue(s, "p90", "grossRevenue"),
    getNativeValue: (s) => getSiteNativeRevenue(s, "p90", "grossRevenue"),
  },
  licenseArbitrage: {
    label: "License Gross Upfront Revenue",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    filter: (s) => s.investmentModel === "license",
    getValue: (s) => getSiteGbpRevenue(s, "p90", "arbitrage"),
    getNativeValue: (s) => getSiteNativeRevenue(s, "p90", "arbitrage"),
  },
  leaseGrossRevenue: {
    label: "Lease Gross Contracted Annual Revenue",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    filter: (s) => s.investmentModel === "lease",
    getValue: (s) => getSiteGbpRevenue(s, "p90", "grossRevenue"),
    getNativeValue: (s) => getSiteNativeRevenue(s, "p90", "grossRevenue"),
  },
  leaseArbitrage: {
    label: "Lease Gross Upfront Revenue",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    filter: (s) => s.investmentModel === "lease",
    getValue: (s) => getSiteGbpRevenue(s, "p90", "arbitrage"),
    getNativeValue: (s) => getSiteNativeRevenue(s, "p90", "arbitrage"),
  },
  licenseNetArr: {
    label: "License Net ARR",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    filter: (s) => s.investmentModel === "license",
    getValue: (s) => getSiteGbpRevenue(s, "p90", "licenseNetArr"),
    getNativeValue: (s) => getSiteNativeRevenue(s, "p90", "licenseNetArr"),
  },
  leaseNetArr: {
    label: "Lease Net ARR",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    filter: (s) =>
      s.investmentModel === "lease" &&
      getSiteGbpRevenue(s, "p90", "leaseNetArr") > 0,
    getValue: (s) => getSiteGbpRevenue(s, "p90", "leaseNetArr"),
    getNativeValue: (s) => getSiteNativeRevenue(s, "p90", "leaseNetArr"),
  },
  totalNetArr: {
    label: "Total Net ARR",
    key: "name",
    format: (v: number, cc: string) => formatCurrency(v, cc, 0, false),
    filter: (s) =>
      s.investmentModel === "license" ||
      (s.investmentModel === "lease" &&
        getSiteGbpRevenue(s, "p90", "leaseNetArr") > 0),
  },
  licenseLease: {
    label: "Investment model",
    key: "investmentModel",
    format: (v: string | null) => v ?? "N/A",
    filter: (s) =>
      s.investmentModel === "license" || s.investmentModel === "lease",
  },
  landlordsTenants: {
    label: "Landlord",
    key: "landlordName",
    format: (v: string | null) => v ?? "",
  },
  performanceRed: {
    label: "Performance",
    key: "performanceRyg",
    format: (v: string) => v,
    filter: (s) => s.performanceRyg === "red",
  },
  performanceAmber: {
    label: "Performance",
    key: "performanceRyg",
    format: (v: string) => v,
    filter: (s) => s.performanceRyg === "amber",
  },
  performanceGreen: {
    label: "Performance",
    key: "performanceRyg",
    format: (v: string) => v,
    filter: (s) => s.performanceRyg === "green" || s.performanceRyg === "blue",
  },
  currentIssues: {
    label: "Current health",
    key: "currentHealthStatus",
    format: (v: string | null) => v ?? "N/A",
    filter: (s) => s.currentHealthStatus === "unhealthy",
  },
  currentlyHealthy: {
    label: "Current health",
    key: "currentHealthStatus",
    format: (v: string | null) => v ?? "N/A",
    filter: (s) => s.currentHealthStatus === "healthy",
  },
  lastMonthIssues: {
    label: "Last month analysis",
    key: "lastMonthHealthStatus",
    format: (v: string | null) => v ?? "N/A",
    filter: (s) => s.lastMonthHealthStatus === "unhealthy",
  },
  lastMonthHealthy: {
    label: "Last month analysis",
    key: "lastMonthHealthStatus",
    format: (v: string | null) => v ?? "N/A",
    filter: (s) => s.lastMonthHealthStatus === "healthy",
  },
};

// ── P50/P90 dual-column tiles ───────────────────────────────────────

interface DualProbColumn {
  format: (v: number) => string;
  getP50Value: (s: SiteMetrics) => number;
  getP90Value: (s: SiteMetrics) => number;
  getWeightValue?: (s: SiteMetrics) => number;
  getNativeP50Value?: (s: SiteMetrics) => number | null;
  getNativeP90Value?: (s: SiteMetrics) => number | null;
  // When true, the P50 column is rendered as a dash (per-row and total).
  // Used for metrics whose source value has no P50/P90 split.
  blankP50?: boolean;
}

const DUAL_PROB_COLUMNS: Record<string, DualProbColumn> = {
  generationMwh: {
    getP50Value: (s) => s.energy?.p50?.generationMwh ?? 0,
    getP90Value: (s) => s.energy?.p90?.generationMwh ?? 0,
    format: (v) => formatNumber(v, 1),
  },
  deliveredOnSitePct: {
    getP50Value: (s) => s.energy?.p50?.deliveredOnSitePct ?? 0,
    getP90Value: (s) => s.energy?.p90?.deliveredOnSitePct ?? 0,
    format: (v) => formatPct(v),
    getWeightValue: (s) => s.energy?.p90?.generationMwh ?? 0,
  },
  demandFulfilledByIrPct: {
    getP50Value: (s) => s.energy?.p50?.demandFulfilledByIrPct ?? 0,
    getP90Value: (s) => s.energy?.p90?.demandFulfilledByIrPct ?? 0,
    format: (v) => formatPct(v),
    getWeightValue: (s) => s.energy?.p50?.demandMwh ?? 0,
  },
  grossRevenue: {
    getP50Value: (s) => getSiteGbpRevenue(s, "p50", "grossRevenue"),
    getP90Value: (s) => getSiteGbpRevenue(s, "p90", "grossRevenue"),
    getNativeP50Value: (s) => getSiteNativeRevenue(s, "p50", "grossRevenue"),
    getNativeP90Value: (s) => getSiteNativeRevenue(s, "p90", "grossRevenue"),
    format: (v) => formatCurrency(v, "GBP", 0, false),
  },
  licenseNetArr: {
    getP50Value: (s) => getSiteGbpRevenue(s, "p50", "licenseNetArr"),
    getP90Value: (s) => getSiteGbpRevenue(s, "p90", "licenseNetArr"),
    getNativeP50Value: (s) => getSiteNativeRevenue(s, "p50", "licenseNetArr"),
    getNativeP90Value: (s) => getSiteNativeRevenue(s, "p90", "licenseNetArr"),
    format: (v) => formatCurrency(v, "GBP", 0, false),
  },
  leaseNetArr: {
    getP50Value: () => 0,
    getP90Value: (s) => getSiteGbpRevenue(s, "p90", "leaseNetArr"),
    getNativeP50Value: () => null,
    getNativeP90Value: (s) => getSiteNativeRevenue(s, "p90", "leaseNetArr"),
    format: (v) => formatCurrency(v, "GBP", 0, false),
    blankP50: true,
  },
  totalNetArr: {
    getP50Value: (s) =>
      getSiteGbpRevenue(s, "p50", "licenseNetArr") +
      getSiteGbpRevenue(s, "p90", "leaseNetArr"),
    getP90Value: (s) =>
      getSiteGbpRevenue(s, "p90", "licenseNetArr") +
      getSiteGbpRevenue(s, "p90", "leaseNetArr"),
    format: (v) => formatCurrency(v, "GBP", 0, false),
  },
  licenseGrossRevenue: {
    getP50Value: (s) => getSiteGbpRevenue(s, "p50", "grossRevenue"),
    getP90Value: (s) => getSiteGbpRevenue(s, "p90", "grossRevenue"),
    getNativeP50Value: (s) => getSiteNativeRevenue(s, "p50", "grossRevenue"),
    getNativeP90Value: (s) => getSiteNativeRevenue(s, "p90", "grossRevenue"),
    format: (v) => formatCurrency(v, "GBP", 0, false),
  },
  leaseGrossRevenue: {
    getP50Value: (s) => getSiteGbpRevenue(s, "p50", "grossRevenue"),
    getP90Value: (s) => getSiteGbpRevenue(s, "p90", "grossRevenue"),
    getNativeP50Value: (s) => getSiteNativeRevenue(s, "p50", "grossRevenue"),
    getNativeP90Value: (s) => getSiteNativeRevenue(s, "p90", "grossRevenue"),
    format: (v) => formatCurrency(v, "GBP", 0, false),
  },
};

// ── Forecast comparison configs ─────────────────────────────────────

interface ForecastColConfig {
  label: string;
  sortKeyActual: string;
  sortKeyPctP50: string;
  sortKeyPctP90: string;
  getActual: (s: SiteMetrics) => number | undefined;
  getForecastP50: (s: SiteMetrics) => number | undefined;
  getForecastP90: (s: SiteMetrics) => number | undefined;
  getPctP50: (s: SiteMetrics) => number | null | undefined;
  getPctP90: (s: SiteMetrics) => number | null | undefined;
  rygKey: keyof SiteMetrics;
  isDemand?: boolean;
  isPctPoints?: boolean;
  // When true, render the forecast and actual values as currency (with the
  // site's native currency in parentheses where applicable) instead of MWh.
  isCurrency?: boolean;
  // Per-site native value accessors so the currency renderer can show the
  // native amount alongside the GBP-normalised one.
  getActualNative?: (s: SiteMetrics) => number | null;
  getForecastP50Native?: (s: SiteMetrics) => number | null;
  getForecastP90Native?: (s: SiteMetrics) => number | null;
}

const FORECAST_COLUMNS: Record<string, ForecastColConfig> = {
  cumulativeGenerationPct: {
    label: "Generation",
    sortKeyActual: "forecastGenerationActual",
    sortKeyPctP50: "cumulativeGenerationPctP50",
    sortKeyPctP90: "cumulativeGenerationPctP90",
    getActual: (s) => s.energy?.actual?.cumulativeGenerationMwh,
    getForecastP50: (s) => s.energy?.p50?.cumulativeGenerationMwh,
    getForecastP90: (s) => s.energy?.p90?.cumulativeGenerationMwh,
    getPctP50: (s) => s.energy?.p50?.cumulativeGenerationPct,
    getPctP90: (s) => s.energy?.p90?.cumulativeGenerationPct,
    rygKey: "forecastGenerationRyg",
  },
  cumulativeDemandPct: {
    label: "Demand",
    sortKeyActual: "forecastDemandActual",
    sortKeyPctP50: "cumulativeDemandPctP50",
    sortKeyPctP90: "cumulativeDemandPctP90",
    getActual: (s) => s.energy?.actual?.cumulativeDemandMwh,
    getForecastP50: (s) => s.energy?.p50?.cumulativeDemandMwh,
    getForecastP90: (s) => s.energy?.p90?.cumulativeDemandMwh,
    getPctP50: (s) => s.energy?.p50?.cumulativeDemandPct,
    getPctP90: (s) => s.energy?.p90?.cumulativeDemandPct,
    rygKey: "forecastDemandRyg",
    isDemand: true,
  },
  cumulativeOnSitePct: {
    label: "On-site",
    sortKeyActual: "forecastOnSiteActual",
    sortKeyPctP50: "cumulativeOnSitePctP50",
    sortKeyPctP90: "cumulativeOnSitePctP90",
    getActual: (s) => s.energy?.actual?.cumulativeOnSiteMwh,
    getForecastP50: (s) => s.energy?.p50?.cumulativeOnSiteMwh,
    getForecastP90: (s) => s.energy?.p90?.cumulativeOnSiteMwh,
    getPctP50: (s) => s.energy?.p50?.cumulativeOnSitePct,
    getPctP90: (s) => s.energy?.p90?.cumulativeOnSitePct,
    rygKey: "forecastOnSiteRyg",
  },
  cumulativeDeliveredOnSitePctPoints: {
    label: "Delivered on-site %",
    sortKeyActual: "forecastDeliveredOnSitePctActual",
    sortKeyPctP50: "cumulativeDeliveredOnSitePctPointsP50",
    sortKeyPctP90: "cumulativeDeliveredOnSitePctPointsP90",
    getActual: (s) => {
      const gen = s.energy?.actual?.cumulativeGenerationMwh;
      const bm = s.energy?.actual?.cumulativeOnSiteMwh ?? 0;
      return gen && gen > 0 ? (bm / gen) * 100 : undefined;
    },
    getForecastP50: (s) => {
      const gen = s.energy?.p50?.cumulativeGenerationMwh;
      const bm = s.energy?.p50?.cumulativeOnSiteMwh ?? 0;
      return gen && gen > 0 ? (bm / gen) * 100 : undefined;
    },
    getForecastP90: (s) => {
      const gen = s.energy?.p90?.cumulativeGenerationMwh;
      const bm = s.energy?.p90?.cumulativeOnSiteMwh ?? 0;
      return gen && gen > 0 ? (bm / gen) * 100 : undefined;
    },
    getPctP50: (s) => s.energy?.p50?.cumulativeDeliveredOnSitePctPoints,
    getPctP90: (s) => s.energy?.p90?.cumulativeDeliveredOnSitePctPoints,
    rygKey: "forecastDeliveredOnSitePctRyg",
    isPctPoints: true,
  },
  cumulativeExportPct: {
    label: "Export",
    sortKeyActual: "forecastExportActual",
    sortKeyPctP50: "cumulativeExportPctP50",
    sortKeyPctP90: "cumulativeExportPctP90",
    getActual: (s) => s.energy?.actual?.cumulativeExportMwh,
    getForecastP50: (s) => s.energy?.p50?.cumulativeExportMwh,
    getForecastP90: (s) => s.energy?.p90?.cumulativeExportMwh,
    getPctP50: (s) => s.energy?.p50?.cumulativeExportPct,
    getPctP90: (s) => s.energy?.p90?.cumulativeExportPct,
    rygKey: "forecastExportRyg",
  },
  cumulativeCustomerRevenue: {
    label: "Customer revenue",
    sortKeyActual: "cumulativeCustomerRevenueActual",
    sortKeyPctP50: "cumulativeCustomerRevenuePctP50",
    sortKeyPctP90: "cumulativeCustomerRevenuePctP90",
    getActual: (s) => s.cumulativeCustomerRevenue?.GBP,
    getForecastP50: (s) => s.cumulativeCustomerRevenueP50?.GBP,
    getForecastP90: (s) => s.cumulativeCustomerRevenueP90?.GBP,
    getPctP50: (s) => s.cumulativeCustomerRevenuePctP50,
    getPctP90: (s) => s.cumulativeCustomerRevenuePctP90,
    rygKey: "forecastCustomerRevenueRyg",
    isCurrency: true,
    getActualNative: (s) =>
      s.currencyCode && s.currencyCode !== "GBP"
        ? (s.cumulativeCustomerRevenue?.[s.currencyCode] ?? null)
        : null,
    getForecastP50Native: (s) =>
      s.currencyCode && s.currencyCode !== "GBP"
        ? (s.cumulativeCustomerRevenueP50?.[s.currencyCode] ?? null)
        : null,
    getForecastP90Native: (s) =>
      s.currencyCode && s.currencyCode !== "GBP"
        ? (s.cumulativeCustomerRevenueP90?.[s.currencyCode] ?? null)
        : null,
  },
};

const FORECAST_METRIC_KEYS = new Set(Object.keys(FORECAST_COLUMNS));
const PERFORMANCE_METRIC_KEYS = new Set([
  "performanceRed",
  "performanceAmber",
  "performanceGreen",
]);

const PERFORMANCE_COLS: ForecastColConfig[] = [
  FORECAST_COLUMNS.cumulativeGenerationPct,
  FORECAST_COLUMNS.cumulativeDemandPct,
  FORECAST_COLUMNS.cumulativeOnSitePct,
  FORECAST_COLUMNS.cumulativeExportPct,
];

const findForecastSortAccessor = (
  sortKey: string
): ((s: SiteMetrics) => number | null | undefined) | undefined => {
  for (const col of Object.values(FORECAST_COLUMNS)) {
    if (sortKey === col.sortKeyActual) return col.getActual;
    if (sortKey === col.sortKeyPctP50) return col.getPctP50;
    if (sortKey === col.sortKeyPctP90) return col.getPctP90;
  }
  return undefined;
};

// ── InProgressSitesPanel ─────────────────────────────────────────────

const InProgressSitesPanel = ({
  sites,
  onClose,
  tileLabel,
}: {
  sites: SiteMetrics[];
  onClose: () => void;
  tileLabel: string;
}) => {
  const [sortField, setSortField] = useState<SortField>("status");
  const [sortAsc, setSortAsc] = useState(false);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(field === "name");
    }
  };

  const sortProps = { sortField, sortAsc, onToggle: toggleSort };

  const sorted = useMemo(() => {
    const strCmp = (aVal: string, bVal: string) => {
      const cmp = aVal.localeCompare(bVal);
      return sortAsc ? cmp : -cmp;
    };
    const numCmp = (aVal: number, bVal: number) => {
      const cmp = aVal - bVal;
      return sortAsc ? cmp : -cmp;
    };
    const statusWeight = (s: string | null | undefined): number =>
      STATUS_WEIGHTS[s as keyof typeof STATUS_WEIGHTS] ?? 0;
    return [...sites].sort((a, b) => {
      if (sortField === "name") return strCmp(a.name, b.name);
      if (sortField === "p50") return numCmp(a.pvKw ?? 0, b.pvKw ?? 0);
      if (sortField === "p90")
        return numCmp(a.batteryMwh ?? 0, b.batteryMwh ?? 0);
      if (sortField === "status") {
        const cmp =
          statusWeight(a.operationalStatus) - statusWeight(b.operationalStatus);
        if (cmp !== 0) return sortAsc ? cmp : -cmp;
        return (a.cod ?? "").localeCompare(b.cod ?? "");
      }
      return strCmp(a.cod ?? "", b.cod ?? "");
    });
  }, [sites, sortField, sortAsc]);

  const formatStatus = (status?: string | null) => {
    if (!status) return "";
    return STATUS_OPTIONS[status as keyof typeof STATUS_OPTIONS] ?? status;
  };

  return (
    <Offcanvas
      show
      onHide={onClose}
      placement="bottom"
      backdrop={false}
      scroll={true}
      enforceFocus={false}
      className="no-transition"
      style={{ height: "50vh" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {tileLabel} ({sorted.length} site
          {sorted.length !== 1 ? "s" : ""})
          <Link
            to="/site?production=true&inprogress=true&sort=cod&sortDir=Desc"
            style={{ fontSize: "0.85rem", marginLeft: "12px" }}
          >
            View on full site list
          </Link>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body style={{ overflow: "auto", padding: "0 16px 16px" }}>
        <Table size="sm">
          <thead>
            <tr>
              <SortableHeader
                field="name"
                label="Site"
                align="left"
                {...sortProps}
              />
              <th style={{ textAlign: "left" }}>Investment model</th>
              <SortableHeader field="p50" label="PV kW" {...sortProps} />
              <SortableHeader field="p90" label="Battery MWh" {...sortProps} />
              <SortableHeader
                field="status"
                label="Status"
                align="left"
                {...sortProps}
              />
              <SortableHeader
                field="cod"
                label="COD"
                align="left"
                {...sortProps}
              />
            </tr>
          </thead>
          <tbody>
            {sorted.map((site) => (
              <tr key={site.id}>
                <td>
                  <Link to={`/site/${site.id}/edit`}>{site.name}</Link>
                </td>
                <td>{site.investmentModel ?? ""}</td>
                <td style={{ textAlign: "right" }}>
                  {formatNumber(site.pvKw ?? 0, 1)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {formatNumber(site.batteryMwh ?? 0, 2)}
                </td>
                <td>{formatStatus(site.operationalStatus)}</td>
                <td>{formatDate(site.cod)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: "bold" }}>
              <td>Total</td>
              <td />
              <td style={{ textAlign: "right" }}>
                {formatNumber(
                  sites.reduce((sum, s) => sum + (s.pvKw ?? 0), 0),
                  1
                )}
              </td>
              <td style={{ textAlign: "right" }}>
                {formatNumber(
                  sites.reduce((sum, s) => sum + (s.batteryMwh ?? 0), 0),
                  2
                )}
              </td>
              <td />
              <td />
            </tr>
          </tfoot>
        </Table>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

// ── SiteBreakdownPanel ──────────────────────────────────────────────

const SiteBreakdownPanel = ({
  metricKey,
  sites,
  inProgressSites,
  currencyCode,
  onClose,
  tileLabel,
}: {
  metricKey: string;
  sites: SiteMetrics[];
  inProgressSites?: SiteMetrics[];
  currencyCode: string;
  onClose: () => void;
  tileLabel: string;
}) => {
  const column = METRIC_COLUMNS[metricKey];
  const dualProb = DUAL_PROB_COLUMNS[metricKey];
  const forecastCol = FORECAST_COLUMNS[metricKey];
  const isOperationalSites = metricKey === "operationalSites";
  const isInProgressSites = metricKey === "inProgressSites";
  const isForecast = FORECAST_METRIC_KEYS.has(metricKey);
  const isMetered = metricKey === "meteredVsSupplierExport";
  const isPerformance = PERFORMANCE_METRIC_KEYS.has(metricKey);
  const isLandlordsTenants = metricKey === "landlordsTenants";
  const isCurrentHealth =
    metricKey === "currentIssues" || metricKey === "currentlyHealthy";
  const isLastMonthHealth =
    metricKey === "lastMonthIssues" || metricKey === "lastMonthHealthy";
  const isHealth = isCurrentHealth || isLastMonthHealth;
  const isDualProb = !!dualProb;
  const showTakeOrPay =
    (metricKey === "cumulativeDemandPct" ||
      metricKey === "cumulativeOnSitePct" ||
      metricKey === "cumulativeExportPct") &&
    sites.some((s) => s.takeOrPayMinKwh != null);
  // The "healthy" panels list sites that are fine, so RYG sorting is
  // meaningless — defaulting to COD descending puts the newest sites first,
  // matching how operators triage the rest of the deployment-stats page.
  const isHealthyOnlyPanel =
    metricKey === "currentlyHealthy" || metricKey === "lastMonthHealthy";
  const isUnhealthyPanel =
    metricKey === "currentIssues" || metricKey === "lastMonthIssues";
  const [sortField, setSortField] = useState<SortField>(
    isHealthyOnlyPanel
      ? "cod"
      : isUnhealthyPanel
        ? ("sort_healthEvents" as SortField)
        : isForecast || isPerformance || isMetered
          ? "ryg"
          : isOperationalSites || isInProgressSites || isLandlordsTenants
            ? "cod"
            : isDualProb
              ? "p50"
              : "metric"
  );
  const [sortAsc, setSortAsc] = useState(
    !isHealthyOnlyPanel &&
      !isUnhealthyPanel &&
      (isForecast || isPerformance || isMetered)
  );
  const [showAlert, setShowAlert] = useState(true);

  const filteredSites = useMemo(() => {
    let result = sites;
    if (column?.filter) {
      result = result.filter(column.filter);
    }
    if (isMetered) {
      result = result.filter((s) => s.hasGridMeteredData === true);
    }
    return result;
  }, [sites, column, isMetered]);

  const sortedSites = useMemo(() => {
    const numCmp = (aVal: number, bVal: number) => {
      const cmp = aVal - bVal;
      return sortAsc ? cmp : -cmp;
    };
    const strCmp = (aVal: string, bVal: string) => {
      const cmp = aVal.localeCompare(bVal);
      return sortAsc ? cmp : -cmp;
    };

    return [...filteredSites].sort((a, b) => {
      if (sortField === "name") return strCmp(a.name, b.name);
      if (sortField === "cod") return strCmp(a.cod ?? "", b.cod ?? "");
      if (sortField === "months")
        return numCmp(a.monthsAnalysed ?? 0, b.monthsAnalysed ?? 0);
      if (sortField === "status") {
        const statusOrder: Record<string, number> = {
          UNHEALTHY: 0,
          UNKNOWN: 1,
          HEALTHY: 2,
        };
        const aS = statusOrder[(a.healthStatus ?? "").toUpperCase()] ?? 2;
        const bS = statusOrder[(b.healthStatus ?? "").toUpperCase()] ?? 2;
        return numCmp(aS, bS);
      }
      if (sortField === "ryg") {
        const rygKeyToUse = isMetered
          ? "meteredVsSupplierExportRyg"
          : (forecastCol?.rygKey ?? "performanceRyg");
        const aRyg = RYG_ORDER[(a[rygKeyToUse] as string) ?? "green"] ?? 2;
        const bRyg = RYG_ORDER[(b[rygKeyToUse] as string) ?? "green"] ?? 2;
        if (aRyg !== bRyg) {
          const cmp = aRyg - bRyg;
          return sortAsc ? cmp : -cmp;
        }
        // Single-metric forecast: secondary sort by P50 pct (most negative first)
        if (forecastCol) {
          const getPctP50 = forecastCol.getPctP50;
          const aPct = (getPctP50(a) ?? 0) as number;
          const bPct = (getPctP50(b) ?? 0) as number;
          return aPct - bPct;
        }
        // Performance / metered: secondary sort by COD (newer sites last)
        return (a.cod ?? "").localeCompare(b.cod ?? "");
      }
      if (sortField === "p50" && dualProb)
        return numCmp(dualProb.getP50Value(a), dualProb.getP50Value(b));
      if (sortField === "p90" && dualProb)
        return numCmp(dualProb.getP90Value(a), dualProb.getP90Value(b));
      if (sortField === "metric" && isForecast && forecastCol)
        return numCmp(
          (forecastCol.getPctP90(a) ?? 0) as number,
          (forecastCol.getPctP90(b) ?? 0) as number
        );
      if (sortField.startsWith("sort_")) {
        const sortKey = sortField.slice(5);
        const forecastAccessor = findForecastSortAccessor(sortKey);
        if (forecastAccessor) {
          return numCmp(
            (forecastAccessor(a) ?? 0) as number,
            (forecastAccessor(b) ?? 0) as number
          );
        }
        if (sortKey === "curtailmentLossMwh") {
          return numCmp(a.curtailmentLossMwh ?? 0, b.curtailmentLossMwh ?? 0);
        }
        if (sortKey === "cumulativeCustomerRevenue") {
          // Sort by GBP figure for consistent ordering across mixed-currency sites.
          return numCmp(
            a.cumulativeCustomerRevenue?.GBP ?? 0,
            b.cumulativeCustomerRevenue?.GBP ?? 0
          );
        }
        if (sortKey === "cumulativeCustomerRevenuePctP50") {
          return numCmp(
            a.cumulativeCustomerRevenuePctP50 ?? 0,
            b.cumulativeCustomerRevenuePctP50 ?? 0
          );
        }
        if (sortKey === "cumulativeCustomerRevenuePctP90") {
          return numCmp(
            a.cumulativeCustomerRevenuePctP90 ?? 0,
            b.cumulativeCustomerRevenuePctP90 ?? 0
          );
        }
        if (sortKey === "latestMonthAnalysed") {
          return (a.latestMonthAnalysed ?? "").localeCompare(
            b.latestMonthAnalysed ?? ""
          );
        }
        if (sortKey === "healthEvents") {
          // Sort by worst severity in the relevant event list, then by count.
          // Higher score = worse, so descending sort puts red first.
          const eventsOf = (s: SiteMetrics) =>
            (isCurrentHealth
              ? s.healthDetail?.liveEvents
              : s.healthDetail?.monthlyEvents) ?? [];
          const worstScore = (s: SiteMetrics) => {
            const evs = eventsOf(s);
            if (evs.some((e) => e.severity === "red")) return 3;
            if (evs.some((e) => e.severity === "amber")) return 2;
            if (evs.length > 0) return 1;
            return 0;
          };
          const aw = worstScore(a);
          const bw = worstScore(b);
          if (aw !== bw) return numCmp(aw, bw);
          return numCmp(eventsOf(a).length, eventsOf(b).length);
        }
        const siteKey = sortKey as keyof SiteMetrics;
        const rygVal = a[siteKey] as string;
        if (
          rygVal === "red" ||
          rygVal === "amber" ||
          rygVal === "green" ||
          (b[siteKey] as string) === "red" ||
          (b[siteKey] as string) === "amber" ||
          (b[siteKey] as string) === "green"
        ) {
          const aRyg = RYG_ORDER[(a[siteKey] as string) ?? "green"] ?? 2;
          const bRyg = RYG_ORDER[(b[siteKey] as string) ?? "green"] ?? 2;
          if (aRyg !== bRyg) {
            const cmp = aRyg - bRyg;
            return sortAsc ? cmp : -cmp;
          }
          const perfCol = PERFORMANCE_COLS.find((c) => c.rygKey === siteKey);
          if (perfCol) {
            return (
              ((perfCol.getPctP90(a) ?? 0) as number) -
              ((perfCol.getPctP90(b) ?? 0) as number)
            );
          }
          return 0;
        }
        const aSortVal = a[siteKey];
        const bSortVal = b[siteKey];
        if (typeof aSortVal === "string" || typeof bSortVal === "string") {
          return strCmp((aSortVal as string) ?? "", (bSortVal as string) ?? "");
        }
        return numCmp((aSortVal as number) ?? 0, (bSortVal as number) ?? 0);
      }
      const aVal =
        (column ? (column.getValue ? column.getValue(a) : a[column.key]) : 0) ??
        0;
      const bVal =
        (column ? (column.getValue ? column.getValue(b) : b[column.key]) : 0) ??
        0;
      if (typeof aVal === "string" && typeof bVal === "string")
        return strCmp(aVal, bVal);
      return numCmp(aVal as number, bVal as number);
    });
  }, [
    filteredSites,
    sortField,
    sortAsc,
    column,
    isForecast,
    isMetered,
    isCurrentHealth,
    forecastCol,
    dualProb,
  ]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(field === "name");
    }
  };

  const showSimpleMetricColumn =
    column &&
    !isOperationalSites &&
    !isLandlordsTenants &&
    !isForecast &&
    !isPerformance &&
    !isHealth &&
    !isDualProb;

  const dualProbTotals = useMemo(() => {
    if (!isDualProb || !dualProb) return null;
    if (dualProb.getWeightValue) {
      let weightedSumP50 = 0;
      let weightedSumP90 = 0;
      let totalWeight = 0;
      for (const site of filteredSites) {
        const w = dualProb.getWeightValue(site);
        weightedSumP50 += dualProb.getP50Value(site) * w;
        weightedSumP90 += dualProb.getP90Value(site) * w;
        totalWeight += w;
      }
      return {
        totalP50: totalWeight > 0 ? weightedSumP50 / totalWeight : 0,
        totalP90: totalWeight > 0 ? weightedSumP90 / totalWeight : 0,
      };
    }
    let totalP50 = 0;
    let totalP90 = 0;
    for (const site of filteredSites) {
      totalP50 += dualProb.getP50Value(site);
      totalP90 += dualProb.getP90Value(site);
    }
    return { totalP50, totalP90 };
  }, [isDualProb, dualProb, filteredSites]);

  const SIMPLE_TOTAL_KEYS = new Set([
    "pvMw",
    "batteryMwh",
    "demandMwh",
    "arbitrage",
    "licenseArbitrage",
    "leaseArbitrage",
  ]);
  const showSimpleTotal = SIMPLE_TOTAL_KEYS.has(metricKey) && column;

  const simpleTotal = useMemo(() => {
    if (!showSimpleTotal || !column) return 0;
    return filteredSites.reduce((sum, site) => {
      const val = column.getValue
        ? column.getValue(site)
        : (site[column.key] as number);
      return sum + ((val as number) ?? 0);
    }, 0);
  }, [showSimpleTotal, column, filteredSites]);

  const forecastTotals = useMemo(() => {
    if (!isForecast || !forecastCol || isMetered) return null;
    if (forecastCol.isPctPoints) {
      // For pct-point metrics, aggregate as totalBm / totalGen * 100
      let totalActualBm = 0;
      let totalActualGen = 0;
      let totalForeBmP50 = 0;
      let totalForeGenP50 = 0;
      let totalForeBmP90 = 0;
      let totalForeGenP90 = 0;
      for (const site of filteredSites) {
        totalActualBm += site.energy?.actual?.cumulativeOnSiteMwh ?? 0;
        totalActualGen += site.energy?.actual?.cumulativeGenerationMwh ?? 0;
        totalForeBmP50 += site.energy?.p50?.cumulativeOnSiteMwh ?? 0;
        totalForeGenP50 += site.energy?.p50?.cumulativeGenerationMwh ?? 0;
        totalForeBmP90 += site.energy?.p90?.cumulativeOnSiteMwh ?? 0;
        totalForeGenP90 += site.energy?.p90?.cumulativeGenerationMwh ?? 0;
      }
      const totalActual =
        totalActualGen > 0 ? (totalActualBm / totalActualGen) * 100 : 0;
      const totalP50 =
        totalForeGenP50 > 0 ? (totalForeBmP50 / totalForeGenP50) * 100 : 0;
      const totalP90 =
        totalForeGenP90 > 0 ? (totalForeBmP90 / totalForeGenP90) * 100 : 0;
      return {
        totalActual,
        totalP50,
        totalP90,
        exTopActual: 0,
        exTopP50: 0,
        exTopP90: 0,
        hasAnyToP: false,
      };
    }
    let totalActual = 0;
    let totalP50 = 0;
    let totalP90 = 0;
    let exTopActual = 0;
    let exTopP50 = 0;
    let exTopP90 = 0;
    let hasAnyToP = false;
    for (const site of filteredSites) {
      const actual = forecastCol.getActual(site) ?? 0;
      const p50 = forecastCol.getForecastP50(site) ?? 0;
      const p90 = forecastCol.getForecastP90(site) ?? 0;
      totalActual += actual;
      totalP50 += p50;
      totalP90 += p90;
      if (site.takeOrPayMinKwh != null) {
        hasAnyToP = true;
      } else {
        exTopActual += actual;
        exTopP50 += p50;
        exTopP90 += p90;
      }
    }
    return {
      totalActual,
      totalP50,
      totalP90,
      exTopActual,
      exTopP50,
      exTopP90,
      hasAnyToP,
    };
  }, [isForecast, isMetered, forecastCol, filteredSites]);

  // Shorthand for SortableHeader props that are always the same
  const sortProps = { sortField, sortAsc, onToggle: toggleSort };

  const renderAlert = () => {
    if (!showAlert) return null;
    let alertMessage: ReactNode | string | undefined = undefined;
    if (metricKey === "generationMwh") {
      alertMessage =
        'Forecast Y1 generation from each site. See "Annual generation (kWh)" in the Generation section of each site.';
    }
    if (metricKey === "demandMwh") {
      alertMessage =
        'Forecast Y1 demand from each site. See "Annual demand (kWh)" in the Demand section of each site.';
    }
    if (metricKey === "deliveredOnSitePct") {
      alertMessage =
        'Percentage of generation forecast to be delivered on-site in Y1. See "Tenant energy share (%)" in the Demand section of each site.';
    }
    if (metricKey === "demandFulfilledByIrPct") {
      alertMessage =
        'Percentage of on-site demand forecast to be fulfilled by InRange in Y1. See "Total fulfilled by on-site (%)" in the Demand section of each site.';
    }
    if (
      metricKey === "arbitrage" ||
      metricKey === "licenseArbitrage" ||
      metricKey === "leaseArbitrage"
    ) {
      alertMessage =
        'Upfront revenue expected from the installation. See "Upfront arbitrage" in the InRange Financial Performance section of each site.';
    }
    if (
      metricKey === "grossRevenue" ||
      metricKey === "licenseGrossRevenue" ||
      metricKey === "leaseGrossRevenue"
    ) {
      alertMessage =
        'Forecast Y1 gross energy revenue (on-site tariff × on-site kWh + export tariff × export kWh). Does not include tariff arbitrage. See "Gross revenue (Y1)" in the InRange Financial Performance section of each site.';
    }
    if (metricKey === "licenseNetArr") {
      alertMessage =
        'Forecast Y1 net ARR (InRange revshare fraction of gross contracted annual revenue). See "IR net ARR (Y1, License)" in the InRange Financial Performance section of each site.';
    }
    if (metricKey === "leaseNetArr") {
      alertMessage =
        "Forecast Lease net ARR is set manually on a site by site basis to reflect the value of the site in the SPV it resides in. This is not automatically calculated by the platform but uses the manually defined value from the Lease ARR override (£) field on a site by site basis.";
    }
    if (metricKey === "totalNetArr") {
      alertMessage =
        "Total net ARR is the sum of Licence net ARR and Lease net ARR. The lease portion contributes its manually defined P90 value to both the P50 and P90 columns; the licence portion contributes its respective P50 / P90 forecast.";
    }
    if (isMetered) {
      alertMessage = (
        <>
          Grid metered export readings are loaded manually after they become
          available from Axpo. <strong>RYG:</strong> green within ±5% of the
          InRange total, amber if supplier under-counts by 0 – 5% or
          over-counts by 5 – 10%, red if supplier under-counts by more than
          5% or over-counts by more than 10% (asymmetric because
          under-metering is a revenue risk).
        </>
      );
    }
    // RYG threshold descriptions per metric — kept in one place so each
    // panel's info banner explains exactly which classifier it uses.
    const TWO_AXIS_RYG = (
      <>
        <strong>RYG:</strong> worst of two independent checks — actual{" "}
        <em>vs P50</em> (green ≥ 97%, amber 92 – 97%, red &lt; 92%) and
        actual <em>vs P90</em> (green ≥ 105%, amber 95 – 105%, red &lt; 95%).
      </>
    );
    if (metricKey === "cumulativeDeliveredOnSitePctPoints") {
      alertMessage = (
        <>
          <strong>Cumulative trailing-12-month</strong> actual vs forecast
          delivered on-site percentage (behind-meter / generation). Difference
          shown in percentage points. Forecast generation includes curtailment,
          but actual generation is net of curtailment, so actual delivered
          on-site percentage on a site with curtailment will be higher than
          forecast. See the Performance tab on each site for monthly
          breakdowns. {TWO_AXIS_RYG}
        </>
      );
    } else if (metricKey === "cumulativeGenerationPct") {
      alertMessage = (
        <>
          <strong>Cumulative trailing-12-month</strong> actuals vs forecast
          comparison (MWh). <em>Actual MWh</em> is the metered generation. For
          sites with a configured export limit the forecast generation is
          compared against <em>Actual MWh + Est curtailed MWh</em>. The{" "}
          <em>Months</em> column shows how many months of data are being used.
          For sites with an export limit this only includes months where
          readings have been committed for the full month — typically within a
          few days of month end. See the Performance tab on each site for
          monthly breakdowns. {TWO_AXIS_RYG}
        </>
      );
    } else if (metricKey === "cumulativeDemandPct") {
      alertMessage = (
        <>
          <strong>Cumulative trailing-12-month</strong> actual vs forecast
          demand. Take-or-pay sites are excluded — those tenants pay a fixed
          minimum regardless of consumption.{" "}
          <strong>RYG:</strong> graded against <em>P50 forecast</em> only
          (demand has a single forecast — green when actual is within 1% of
          P50, amber 95 – 99%, red &lt; 95%). Over-shooting is treated as
          green.
        </>
      );
    } else if (metricKey === "cumulativeExportPct") {
      alertMessage = (
        <>
          <strong>Cumulative trailing-12-month</strong> actual vs forecast
          export. Per-site RYG is bidirectional because each site&apos;s PPA
          can be breached by over-export. <strong>Per-site (annual export ≥
          100 MWh):</strong> red below P90, amber between P90 and P50,
          green between P50 and +10% / +£1k above P50, amber up to +25% /
          +£10k above, red beyond. <strong>Per-site small exporters
          (annual &lt; 100 MWh):</strong> green ≥ 80% of P50, amber 70 –
          80%, red below; over-export side always green (tiny absolute
          volumes).{" "}
          <strong>Aggregate (the parent tile):</strong> below-only — green
          when total actual ≥ total P50, amber between total P90 and P50,
          red below total P90. There&apos;s no fleet-wide PPA, so total
          over-export isn&apos;t penalised.
        </>
      );
    } else if (metricKey === "cumulativeCustomerRevenue") {
      alertMessage = (
        <>
          <strong>Cumulative trailing-12-month</strong> actual vs forecast
          customer revenue (selected per active investment model + ownership
          to mirror the perf/forecast page). {TWO_AXIS_RYG}{" "}
          <strong>RYG for PV lease sites:</strong> green when actual is
          within 1% of the contracted lease, amber 95 – 99%, red &lt; 95%.
        </>
      );
    } else if (isForecast || isPerformance) {
      alertMessage = (
        <>
          <strong>Cumulative trailing-12-month</strong> actuals vs forecast
          comparison (MWh). Only months with complete actuals are included.
          {isPerformance &&
            " For sites with an export limit this only includes months where readings have been committed for the full month — typically within a few days of month end."}{" "}
          See the Performance tab on each site for monthly breakdowns.
          {isPerformance &&
            " The worst monthly RYG across generation, demand, on-site, and export determines each site's overall rating. Generation comparison is Actual + Estimated curtailed vs forecast."}{" "}
          {isPerformance ? null : TWO_AXIS_RYG}
        </>
      );
    }
    if (isCurrentHealth) {
      alertMessage =
        "Showing live operational health: any health issue currently open. A site is unhealthy if it has at least one open issue right now.";
    }
    if (isLastMonthHealth) {
      alertMessage =
        "Showing the post-billing review of the most recent billed month: issues found when last month's readings were analysed. A site is unhealthy if any issues were flagged against that month.";
    }
    if (alertMessage === undefined) {
      return null;
    }
    return (
      <Alert
        variant="info"
        dismissible
        onClose={() => setShowAlert(false)}
        className="small"
      >
        {alertMessage}
      </Alert>
    );
  };

  if (isInProgressSites && inProgressSites) {
    return (
      <InProgressSitesPanel
        sites={inProgressSites}
        onClose={onClose}
        tileLabel={tileLabel}
      />
    );
  }

  return (
    <Offcanvas
      show
      onHide={onClose}
      placement="bottom"
      backdrop={false}
      scroll={true}
      enforceFocus={false}
      className="no-transition"
      style={{ height: "50vh" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {tileLabel} ({filteredSites.length} site
          {filteredSites.length !== 1 ? "s" : ""})
          {isOperationalSites && (
            <Link
              to="/site?production=true&operational=true&sort=cod&sortDir=Desc"
              style={{ fontSize: "0.85rem", marginLeft: "12px" }}
            >
              View on full site list
            </Link>
          )}
          {isInProgressSites && (
            <Link
              to="/site?production=true&inprogress=true&sort=cod&sortDir=Desc"
              style={{ fontSize: "0.85rem", marginLeft: "12px" }}
            >
              View on full site list
            </Link>
          )}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body style={{ overflow: "auto", padding: "0 16px 16px" }}>
        {renderAlert()}
        <Table size="sm">
          <thead>
            <tr>
              {(isForecast || isPerformance || isMetered) && (
                <SortableHeader
                  field="ryg"
                  label="RYG"
                  align="center"
                  {...sortProps}
                />
              )}
              <SortableHeader
                field="name"
                label="Site"
                align="left"
                {...sortProps}
              />
              {isOperationalSites && (
                <th style={{ textAlign: "left" }}>Investment model</th>
              )}
              {showSimpleMetricColumn && (
                <SortableHeader
                  field="metric"
                  label={column.label}
                  {...sortProps}
                />
              )}
              {isDualProb && (
                <>
                  <SortableHeader
                    field="p50"
                    label={
                      metricKey === "totalNetArr" ? (
                        <>
                          P50{" "}
                          <Tooltip
                            text="The lease portion of Total net ARR is set manually as a P90 value, so it contributes the same value to both the P50 and P90 columns."
                            position="bottom"
                          >
                            <span style={{ cursor: "help" }}>*</span>
                          </Tooltip>
                        </>
                      ) : (
                        "P50"
                      )
                    }
                    {...sortProps}
                  />
                  <SortableHeader field="p90" label="P90" {...sortProps} />
                </>
              )}
              {isForecast && forecastCol && !isMetered && (
                <>
                  <SortableHeader
                    field={`sort_${forecastCol.sortKeyPctP50}` as SortField}
                    label={
                      forecastCol.isDemand
                        ? "Forecast (% diff)"
                        : forecastCol.isPctPoints
                          ? "P50 (pp diff)"
                          : "P50 (% diff)"
                    }
                    {...sortProps}
                  />
                  {!forecastCol.isDemand && (
                    <SortableHeader
                      field={`sort_${forecastCol.sortKeyPctP90}` as SortField}
                      label={
                        forecastCol.isPctPoints
                          ? "P90 (pp diff)"
                          : "P90 (% diff)"
                      }
                      {...sortProps}
                    />
                  )}
                  <SortableHeader
                    field={`sort_${forecastCol.sortKeyActual}` as SortField}
                    label={forecastCol.isPctPoints ? "Actual %" : "Actual MWh"}
                    {...sortProps}
                  />
                  {forecastCol.rygKey === "forecastGenerationRyg" && (
                    <SortableHeader
                      field={"sort_curtailmentLossMwh" as SortField}
                      label="Est curtailed MWh"
                      {...sortProps}
                    />
                  )}
                  {showTakeOrPay && (
                    <SortableHeader
                      field={"sort_takeOrPayMinKwh" as SortField}
                      label="Take or pay MWh"
                      {...sortProps}
                    />
                  )}
                  <SortableHeader
                    field="months"
                    label="Months"
                    {...sortProps}
                  />
                  <SortableHeader
                    field={"sort_latestMonthAnalysed" as SortField}
                    label="Latest month"
                    {...sortProps}
                  />
                  <SortableHeader
                    field="cod"
                    label="COD"
                    align="right"
                    {...sortProps}
                  />
                </>
              )}
              {isMetered && (
                <>
                  <SortableHeader
                    field={
                      "sort_meteredVsSupplierExportInrangeMwh" as SortField
                    }
                    label="InRange MWh"
                    {...sortProps}
                  />
                  <SortableHeader
                    field={"sort_meteredVsSupplierExportGridMwh" as SortField}
                    label="Grid MWh"
                    {...sortProps}
                  />
                  <SortableHeader
                    field="metric"
                    label="Diff %"
                    {...sortProps}
                  />
                  <SortableHeader
                    field={"sort_monthsWithoutGridActuals" as SortField}
                    label="Months behind"
                    {...sortProps}
                  />
                </>
              )}
              {isPerformance && (
                <>
                  {PERFORMANCE_COLS.map((pc) => (
                    <th
                      key={pc.rygKey as string}
                      colSpan={pc.isDemand ? 2 : 3}
                      style={{ textAlign: "left" }}
                    >
                      {pc.label}
                    </th>
                  ))}
                  <th colSpan={3} style={{ textAlign: "left" }}>
                    Revenue
                  </th>
                </>
              )}
              {isCurrentHealth && (
                <SortableHeader
                  field={"sort_healthEvents" as SortField}
                  label="Live status"
                  align="left"
                  {...sortProps}
                />
              )}
              {isLastMonthHealth && (
                <SortableHeader
                  field={"sort_healthEvents" as SortField}
                  label="Last billed month analysis"
                  align="left"
                  {...sortProps}
                />
              )}
              {isLandlordsTenants && (
                <>
                  <SortableHeader
                    field="sort_landlordName"
                    label="Landlord"
                    align="left"
                    {...sortProps}
                  />
                  <SortableHeader
                    field="sort_tenantName"
                    label="Tenant"
                    align="left"
                    {...sortProps}
                  />
                </>
              )}
              {!isForecast && !isPerformance && (
                <SortableHeader
                  field="cod"
                  label="COD"
                  align="right"
                  {...sortProps}
                />
              )}
            </tr>
            {isPerformance && (
              <tr>
                <th />
                <th />
                {PERFORMANCE_COLS.map((pc) => (
                  <React.Fragment key={pc.rygKey as string}>
                    <SortableHeader
                      field={`sort_${pc.rygKey as string}` as SortField}
                      label="RYG"
                      align="center"
                      fontWeight="normal"
                      {...sortProps}
                    />
                    <SortableHeader
                      field={`sort_${pc.sortKeyPctP50}` as SortField}
                      label={pc.isDemand ? "Fore. (%)" : "P50 (%)"}
                      fontWeight="normal"
                      {...sortProps}
                    />
                    {!pc.isDemand && (
                      <SortableHeader
                        field={`sort_${pc.sortKeyPctP90}` as SortField}
                        label="P90 (%)"
                        fontWeight="normal"
                        {...sortProps}
                      />
                    )}
                  </React.Fragment>
                ))}
                <SortableHeader
                  field={"sort_forecastCustomerRevenueRyg" as SortField}
                  label="RYG"
                  align="center"
                  fontWeight="normal"
                  {...sortProps}
                />
                <SortableHeader
                  field={"sort_cumulativeCustomerRevenuePctP50" as SortField}
                  label="P50 (%)"
                  fontWeight="normal"
                  {...sortProps}
                />
                <SortableHeader
                  field={"sort_cumulativeCustomerRevenuePctP90" as SortField}
                  label="P90 (%)"
                  fontWeight="normal"
                  {...sortProps}
                />
              </tr>
            )}
          </thead>
          <tbody>
            {sortedSites.map((site) => (
              <tr key={site.id}>
                {(isForecast || isPerformance || isMetered) && (
                  <td style={{ textAlign: "center" }}>
                    {isPerformance
                      ? RYG_ICONS[site.performanceRyg ?? "green"]
                      : isMetered
                        ? RYG_ICONS[site.meteredVsSupplierExportRyg ?? "green"]
                        : RYG_ICONS[
                            (forecastCol
                              ? (site[forecastCol.rygKey] as string)
                              : undefined) ?? "green"
                          ]}
                  </td>
                )}
                <td>
                  <div>
                    <Link
                      to={
                        isHealth
                          ? `/site/${site.id}/health/status`
                          : `/site/${site.id}/edit`
                      }
                    >
                      {site.name}
                    </Link>
                  </div>
                </td>
                {isOperationalSites && (
                  <td>{site.investmentModel ?? ""}</td>
                )}
                {showSimpleMetricColumn && (
                  <td style={{ textAlign: "right" }}>
                    {formatWithNativeCurrency(
                      column.format(
                        column.getValue
                          ? column.getValue(site)
                          : site[column.key],
                        currencyCode
                      ),
                      column.getNativeValue?.(site),
                      site.currencyCode
                    )}
                  </td>
                )}
                {isDualProb && dualProb && (
                  <>
                    <td style={{ textAlign: "right" }}>
                      {dualProb.blankP50
                        ? "—"
                        : formatWithNativeCurrency(
                            dualProb.format(dualProb.getP50Value(site)),
                            dualProb.getNativeP50Value?.(site),
                            site.currencyCode
                          )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatWithNativeCurrency(
                        dualProb.format(dualProb.getP90Value(site)),
                        dualProb.getNativeP90Value?.(site),
                        site.currencyCode
                      )}
                    </td>
                  </>
                )}
                {isForecast && forecastCol && !isMetered && (
                  <>
                    <td style={{ textAlign: "right" }}>
                      {forecastCol.isCurrency
                        ? fmtCurrencyWithPct(
                            forecastCol.getForecastP50(site),
                            forecastCol.getForecastP50Native?.(site),
                            forecastCol.getPctP50(site),
                            site.currencyCode
                          )
                        : forecastCol.isPctPoints
                          ? fmtPctWithPpDiff(
                              forecastCol.getForecastP50(site),
                              forecastCol.getPctP50(site)
                            )
                          : fmtWithPct(
                              forecastCol.getForecastP50(site),
                              forecastCol.getPctP50(site)
                            )}
                    </td>
                    {!forecastCol.isDemand && (
                      <td style={{ textAlign: "right" }}>
                        {forecastCol.isCurrency
                          ? fmtCurrencyWithPct(
                              forecastCol.getForecastP90(site),
                              forecastCol.getForecastP90Native?.(site),
                              forecastCol.getPctP90(site),
                              site.currencyCode
                            )
                          : forecastCol.isPctPoints
                            ? fmtPctWithPpDiff(
                                forecastCol.getForecastP90(site),
                                forecastCol.getPctP90(site)
                              )
                            : fmtWithPct(
                                forecastCol.getForecastP90(site),
                                forecastCol.getPctP90(site)
                              )}
                      </td>
                    )}
                    <td style={{ textAlign: "right" }}>
                      {forecastCol.isCurrency
                        ? formatWithNativeCurrency(
                            formatCurrency(
                              forecastCol.getActual(site) ?? 0,
                              "GBP",
                              0,
                              false
                            ),
                            forecastCol.getActualNative?.(site),
                            site.currencyCode
                          )
                        : forecastCol.isPctPoints
                          ? formatPct(forecastCol.getActual(site) ?? 0)
                          : formatNumber(forecastCol.getActual(site) ?? 0, 1)}
                    </td>
                    {forecastCol.rygKey === "forecastGenerationRyg" && (
                      <td style={{ textAlign: "right" }}>
                        {site.curtailmentLossMwh != null &&
                        site.curtailmentLossMwh > 0
                          ? formatNumber(site.curtailmentLossMwh, 1)
                          : site.curtailmentPending
                            ? "pending"
                            : "—"}
                      </td>
                    )}
                    {showTakeOrPay && (
                      <td style={{ textAlign: "right" }}>
                        {site.takeOrPayMinKwh != null
                          ? formatNumber(site.takeOrPayMinKwh / 1000, 1)
                          : ""}
                      </td>
                    )}
                    <td style={{ textAlign: "right" }}>
                      {site.monthsAnalysed ?? ""}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {site.latestMonthAnalysed ?? ""}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatDate(site.cod)}
                    </td>
                  </>
                )}
                {isMetered && (
                  <>
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(
                        site.meteredVsSupplierExportInrangeMwh ?? 0,
                        1
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(
                        site.meteredVsSupplierExportGridMwh ?? 0,
                        1
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatPctSigned(site.meteredVsSupplierExportPct ?? null)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {site.monthsWithoutGridActuals ?? ""}
                    </td>
                  </>
                )}
                {isPerformance && (
                  <>
                    {PERFORMANCE_COLS.map((pc) => {
                      const pctP50 = pc.getPctP50(site);
                      const pctP90 = pc.getPctP90(site);
                      const ryg = (site[pc.rygKey] as string) ?? "green";
                      return (
                        <React.Fragment key={pc.rygKey as string}>
                          <td style={{ textAlign: "center" }}>
                            {RYG_ICONS[ryg] ?? ""}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {fmtWithPct(pc.getForecastP50(site), pctP50)}
                          </td>
                          {!pc.isDemand && (
                            <td style={{ textAlign: "right" }}>
                              {fmtWithPct(pc.getForecastP90(site), pctP90)}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {(() => {
                      const cc = site.currencyCode ?? "GBP";
                      const ryg = site.forecastCustomerRevenueRyg ?? "green";
                      return (
                        <>
                          <td style={{ textAlign: "center" }}>
                            {RYG_ICONS[ryg] ?? ""}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {fmtCurrencyWithPct(
                              site.cumulativeCustomerRevenueP50?.GBP,
                              cc !== "GBP"
                                ? (site.cumulativeCustomerRevenueP50?.[cc] ??
                                    null)
                                : null,
                              site.cumulativeCustomerRevenuePctP50,
                              site.currencyCode
                            )}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {fmtCurrencyWithPct(
                              site.cumulativeCustomerRevenueP90?.GBP,
                              cc !== "GBP"
                                ? (site.cumulativeCustomerRevenueP90?.[cc] ??
                                    null)
                                : null,
                              site.cumulativeCustomerRevenuePctP90,
                              site.currencyCode
                            )}
                          </td>
                        </>
                      );
                    })()}
                  </>
                )}
                {isCurrentHealth && (
                  <td>
                    <LiveStatusCell site={site} />
                  </td>
                )}
                {isLastMonthHealth && (
                  <td>
                    <BillingAnalysisCell site={site} />
                  </td>
                )}
                {isLandlordsTenants && (
                  <>
                    <td>
                      {site.isOwnerOccupier ? "" : (site.landlordName ?? "")}
                    </td>
                    <td>{site.tenantName ?? ""}</td>
                  </>
                )}
                {!isForecast && !isPerformance && (
                  <td style={{ textAlign: "right" }}>{formatDate(site.cod)}</td>
                )}
              </tr>
            ))}
            {sortedSites.length === 0 && (
              <tr>
                <td colSpan={99} className="text-muted text-center py-3">
                  No sites
                </td>
              </tr>
            )}
          </tbody>
          {isDualProb && dualProbTotals && dualProb && (
            <tfoot>
              <tr style={{ fontWeight: "bold" }}>
                <td>Total</td>
                <td style={{ textAlign: "right" }}>
                  {dualProb.blankP50
                    ? "—"
                    : dualProb.format(dualProbTotals.totalP50)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {dualProb.format(dualProbTotals.totalP90)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
          {showSimpleTotal && (
            <tfoot>
              <tr style={{ fontWeight: "bold" }}>
                <td>Total</td>
                <td style={{ textAlign: "right" }}>
                  {column.format(simpleTotal, currencyCode)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
          {forecastTotals && (
            <tfoot>
              <tr style={{ fontWeight: "bold" }}>
                {/* RYG + Site columns */}
                <td colSpan={2}>Total</td>
                <td style={{ textAlign: "right" }}>
                  {forecastCol?.isPctPoints
                    ? fmtPctWithPpDiff(
                        forecastTotals.totalP50,
                        forecastTotals.totalP50 > 0
                          ? forecastTotals.totalActual - forecastTotals.totalP50
                          : null
                      )
                    : fmtWithPct(
                        forecastTotals.totalP50,
                        forecastTotals.totalP50 > 0
                          ? ((forecastTotals.totalActual -
                              forecastTotals.totalP50) /
                              forecastTotals.totalP50) *
                              100
                          : null
                      )}
                </td>
                {!forecastCol?.isDemand && (
                  <td style={{ textAlign: "right" }}>
                    {forecastCol?.isPctPoints
                      ? fmtPctWithPpDiff(
                          forecastTotals.totalP90,
                          forecastTotals.totalP90 > 0
                            ? forecastTotals.totalActual -
                                forecastTotals.totalP90
                            : null
                        )
                      : fmtWithPct(
                          forecastTotals.totalP90,
                          forecastTotals.totalP90 > 0
                            ? ((forecastTotals.totalActual -
                                forecastTotals.totalP90) /
                                forecastTotals.totalP90) *
                                100
                            : null
                        )}
                  </td>
                )}
                <td style={{ textAlign: "right" }}>
                  {forecastCol?.isPctPoints
                    ? formatPct(forecastTotals.totalActual)
                    : formatNumber(forecastTotals.totalActual, 1)}
                </td>
                {showTakeOrPay && <td />}
                <td />
                <td />
              </tr>
              {forecastTotals.hasAnyToP &&
                (metricKey === "cumulativeDemandPct" ||
                  metricKey === "cumulativeOnSitePct" ||
                  metricKey === "cumulativeExportPct") && (
                  <tr style={{ fontWeight: "bold" }}>
                    <td colSpan={2}>Total (without take or pay)</td>
                    <td style={{ textAlign: "right" }}>
                      {fmtWithPct(
                        forecastTotals.exTopP50,
                        forecastTotals.exTopP50 > 0
                          ? ((forecastTotals.exTopActual -
                              forecastTotals.exTopP50) /
                              forecastTotals.exTopP50) *
                              100
                          : null
                      )}
                    </td>
                    {!forecastCol?.isDemand && (
                      <td style={{ textAlign: "right" }}>
                        {fmtWithPct(
                          forecastTotals.exTopP90,
                          forecastTotals.exTopP90 > 0
                            ? ((forecastTotals.exTopActual -
                                forecastTotals.exTopP90) /
                                forecastTotals.exTopP90) *
                                100
                            : null
                        )}
                      </td>
                    )}
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(forecastTotals.exTopActual, 1)}
                    </td>
                    {showTakeOrPay && <td />}
                    <td />
                    <td />
                  </tr>
                )}
            </tfoot>
          )}
        </Table>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default SiteBreakdownPanel;
